import {
  Camera,
  Group,
  LinearFilter,
  MagnificationTextureFilter,
  Material,
  Mesh,
  MeshBasicMaterial,
  NotEqualStencilFunc,
  Object3D,
  ReplaceStencilOp,
  Vector3,
  WebGLRenderer,
} from 'three';

import { Levels } from '../../../../../typings/api/skymap/rest/v0/.common';
import { LevelIndex } from './level-index';
import {
  TileCoord,
  TileDesire,
  TileKey,
  tileKey,
  TileTextureLoader,
  WorldMapping,
} from './ortho-tile-typings';
import { TileCache } from './tile-cache';
import { TileLodSelector } from './tile-lod-selector';
import { TileQuadtree } from './tile-quad-tree';

/** True if child is a descendant of parent in the tile quadtree (same coverage area, higher zoom). */
function isDescendant(child: TileCoord, parent: TileCoord): boolean {
  if (child.z <= parent.z) {
    return false;
  }
  const dz = child.z - parent.z;
  return child.x >> dz === parent.x && child.y >> dz === parent.y;
}

/** Max zoom levels deeper than desired to consider for descendant fallback (avoids too many tiny tiles). */
const MAX_DESCENDANT_ZOOM_DELTA = 3;

/** NDC margin for frustum test; tiles slightly outside view still count as intersecting (prefetch padding). */
const FRUSTUM_NDC_MARGIN = 0.15;

/** Frames to keep ready tiles visible after they stop being selected (reduces edge pop during zoom-out). */
const HOLD_FRAMES = 8;

/** Root tiles are always protected from cancellation — use max priority so they load first. */
const ROOT_PRIORITY = 1.0;

/**
 * Added to a tile's renderOrder per zoom level, so finer tiles draw over coarser fallbacks.
 * Small enough that tiles stay within their layer in renderOrders (layers are >= 1 apart).
 */
const ZOOM_RENDER_ORDER_STEP = 0.001;

type OrthoTileAssetParams = {
  id: string;
  levels: Levels[];

  worldMapping: WorldMapping;

  loader: TileTextureLoader;
  cache: TileCache;

  splitThresholdPx?: number;
  maxTiles?: number;
  keepRootsVisible?: boolean;
};

/**
 * Scene node representing ONE orthotile dataset.
 * Always renders at least the lowest-resolution mosaic.
 */
export class OrthoTileAsset extends Object3D {
  private readonly assetId: string;

  private readonly levelIndex: LevelIndex;
  private readonly quadtree: TileQuadtree;
  private readonly lodSelector: TileLodSelector;

  private readonly loader: TileTextureLoader;
  private readonly cache: TileCache;
  private readonly worldMapping: WorldMapping;

  private readonly tilesGroup = new Group();

  private readonly keepRootsVisible: boolean;
  private readonly rootKeys: ReadonlySet<TileKey>;

  private frame = 0;
  private readonly visibleKeys = new Set<TileKey>();
  /** Last frame each tile was selected; used for keepalive so ready tiles stay visible for HOLD_FRAMES. */
  private readonly lastSelectedFrameByKey = new Map<TileKey, number>();
  private readonly failedTiles = new Set<TileKey>(); // Track tiles that failed to load

  private opacity = 1;
  private magFilter: MagnificationTextureFilter = LinearFilter;
  /** The render order of the tiles once only the roots are shown, see showRootsOnly. */
  private rootsOnlyRenderOrder: number | undefined;

  constructor(params: OrthoTileAssetParams) {
    super();

    this.assetId = params.id;
    this.loader = params.loader;
    this.cache = params.cache;
    this.worldMapping = params.worldMapping;

    this.levelIndex = new LevelIndex(params.levels);

    this.quadtree = new TileQuadtree({
      minZoom: this.levelIndex.minZoom,
      maxZoom: this.levelIndex.maxZoom,
      roots: this.levelIndex.getRoots(),
    });

    this.lodSelector = new TileLodSelector({
      splitThresholdPx: params.splitThresholdPx ?? 384,
      maxTiles: params.maxTiles ?? 800,
    });

    this.keepRootsVisible = params.keepRootsVisible ?? false;
    this.rootKeys = new Set(this.quadtree.roots.map(tileKey));

    this.add(this.tilesGroup);
  }

  /**
   * Call every frame (or on camera change)
   */
  update(camera: Camera, renderer: WebGLRenderer): void {
    this.frame++;

    if (this.keepRootsVisible) {
      for (const key of this.rootKeys) {
        this.cache.touch(key);
      }
    }

    this.visibleKeys.clear();

    const desires: TileDesire[] = [];

    this.lodSelector.selectTiles({
      quadtree: this.quadtree,
      levelIndex: this.levelIndex,
      ctx: {
        camera,
        viewport: this.lodSelector.getViewport(renderer),
        estimateScreenPx: (coord) => this.estimateTileScreenSize(coord, camera, renderer),
        intersects: (coord) => this.tileIntersectsFrustum(coord, camera),
        getTileCenter: (coord) => this.getTileCenter(coord),
      },
      out: desires,
    });

    // Build the protected-keys set before cancelling outdated requests.
    // Protected = desired tiles + their fallback ancestors + roots + tiles still in the hold window.
    const protectedKeys = new Set<TileKey>();
    for (const desire of desires) {
      protectedKeys.add(tileKey(desire.coord));
      for (const fb of desire.fallbackChain) {
        protectedKeys.add(tileKey(fb));
      }
    }
    for (const r of this.quadtree.roots) {
      protectedKeys.add(tileKey(r));
    }
    // Hold-window tiles may still be loading — aborting them would prevent them from ever completing.
    for (const [k, f] of this.lastSelectedFrameByKey) {
      if (this.frame - f <= HOLD_FRAMES) {
        protectedKeys.add(k);
      }
    }

    this.cache.cancelOutdated(protectedKeys);

    for (const desire of desires) {
      const priority = this.computeTilePriority(desire.coord, camera);
      this.realizeTile(desire, priority);
    }

    // Apply visibility after processing all desires
    this.applyVisibility();

    // Update keepalive: record this frame for any selected tile; prune expired entries
    for (const k of this.visibleKeys) {
      this.lastSelectedFrameByKey.set(k, this.frame);
    }
    for (const [k, f] of this.lastSelectedFrameByKey) {
      if (this.frame - f > HOLD_FRAMES) {
        this.lastSelectedFrameByKey.delete(k);
      }
    }

    this.cleanup();
  }

  /**
   * Preload root tiles once at startup to ensure fallback coverage.
   * Call this right after creating the asset.
   *
   * @returns Resolves once every root tile has loaded or failed, and so covers all it can.
   */
  preloadRoots(): Promise<void> {
    const loads = this.quadtree.roots
      .filter((r) => this.levelIndex.hasTile(r))
      .map((r) => this.createTile(r, ROOT_PRIORITY));
    return Promise.all(loads).then(() => undefined);
  }

  /**
   * Shows only the root tiles that have loaded from now on, the whole orthophoto at its lowest
   * detail, drawn at the given render order, e.g. under another orthophoto loading in its place.
   * Stops loading, and frees every other tile. The asset must not be updated afterwards.
   */
  showRootsOnly(renderOrder: number): void {
    this.rootsOnlyRenderOrder = renderOrder;
    this.cache.cancelAll();

    const keptKeys = new Set<TileKey>();
    for (const child of [...this.tilesGroup.children]) {
      const mesh = child as Mesh;
      if (this.rootKeys.has(mesh.name) && this.isReady(mesh)) {
        keptKeys.add(mesh.name);
        mesh.visible = true;
        this.applyTileSettings(mesh);
      } else {
        this.worldMapping.disposeTileMesh(mesh);
        this.tilesGroup.remove(mesh);
      }
    }
    this.cache.freeTexturesExcept(keptKeys);
  }

  /**
   * Sets the opacity of all tiles, including the ones loaded later.
   */
  setOpacity(opacity: number): void {
    if (opacity === this.opacity) {
      return;
    }
    this.opacity = opacity;
    this.forEachTile((mesh) => this.applyTileSettings(mesh));
  }

  /**
   * Sets how all tiles are magnified, including the ones loaded later: LinearFilter is smooth,
   * NearestFilter shows plain pixels.
   */
  setMagFilter(magFilter: MagnificationTextureFilter): void {
    if (magFilter === this.magFilter) {
      return;
    }
    this.magFilter = magFilter;
    this.forEachTile((mesh) => this.applyTileSettings(mesh));
  }

  /**
   * Cancels the requests for every tile but the roots, e.g. while the asset is hidden and not
   * updated. The cancelled tiles load again once they are selected.
   */
  cancelLoading(): void {
    this.cache.cancelOutdated(this.rootKeys);
  }

  /**
   * Cancel all pending/inflight requests and free cached textures. Call when removing the asset
   * from the scene; disposeRecursive only reaches textures that are on a tile mesh.
   */
  dispose(): void {
    this.cache.dispose();
  }

  // ------------------------------------------------------------
  // Tile realization & fallback logic
  // ------------------------------------------------------------

  private getMesh(key: string): Mesh | null {
    return this.tilesGroup.getObjectByName(key) as Mesh | null;
  }

  private isReady(mesh: Mesh | null): boolean {
    return !!mesh && mesh.userData?.state === 'ready';
  }

  private ensureTileRequested(coord: TileCoord, priority: number): void {
    const key = tileKey(coord);
    const existing = this.getMesh(key);
    if (existing) {
      // Tile mesh already exists — keep it alive and update priority if still pending
      existing.userData.lastUsedFrame = this.frame;
      this.cache.setPriority(key, priority);
      return;
    }
    // Don't retry tiles that have already failed with a real network error
    if (this.failedTiles.has(key)) {
      return;
    }
    if (this.levelIndex.hasTile(coord)) {
      void this.createTile(coord, priority);
    }
  }

  private realizeTile(desire: TileDesire, priority: number): void {
    // 1) Kick off loading for the *ideal* tile (invisible until ready)
    this.ensureTileRequested(desire.coord, priority);

    // 2) Choose best READY tile among [desired, ...fallbacks] (fallbacks optional)
    const chain = [desire.coord, ...desire.fallbackChain];

    for (const coord of chain) {
      const key = tileKey(coord);
      const mesh = this.getMesh(key);

      // Do NOT request fallback tiles here.
      // Fallbacks should already exist because roots are preloaded and we keep parents around.
      if (!mesh) {
        continue;
      }

      if (this.isReady(mesh)) {
        this.markVisible(mesh);
        return;
      }
    }

    // 2.5) ZOOM-IN fallback: show nearest READY ancestor (parent, grandparent, ...).
    // When the selector stops selecting the parent during split, the parent is no longer
    // in fallbackChain/desired, so we walk the quadtree and show whatever ancestor is already ready.
    let cur = this.quadtree.parentOf(desire.coord);
    while (cur) {
      const key = tileKey(cur);
      const mesh = this.getMesh(key);
      if (mesh && this.isReady(mesh)) {
        this.markVisible(mesh);
        return;
      }
      cur = this.quadtree.parentOf(cur);
    }

    // 3) Descendant fallback: when zooming out, desired (parent) tile may not be ready yet.
    // Show any already-loaded children that cover this area to avoid blink.
    for (const child of this.tilesGroup.children) {
      const mesh = child as Mesh;
      if (!this.isReady(mesh)) {
        continue;
      }

      const c = mesh.userData?.coord as TileCoord | undefined;
      if (!c) {
        continue;
      }

      const zoomDelta = c.z - desire.coord.z;
      if (zoomDelta <= 0 || zoomDelta > MAX_DESCENDANT_ZOOM_DELTA) {
        continue;
      }
      if (!isDescendant(c, desire.coord)) {
        continue;
      }

      this.markVisible(mesh);
    }

    // 4) Nothing ready -> leave empty (rare when descendant fallback is on).
  }

  /** Resolves once the tile has loaded, failed or been cancelled. */
  private createTile(coord: TileCoord, priority: number): Promise<void> {
    const key = tileKey(coord);

    if (this.tilesGroup.getObjectByName(key)) {
      return Promise.resolve();
    }

    const geometry = this.worldMapping.tileToWorldGeometry({
      coord,
    });

    const mesh = this.worldMapping.createTileMesh(geometry);
    mesh.name = key;

    // IMPORTANT: hide until ready, to avoid white flash
    mesh.visible = false;

    const isBackgroundRoot = this.keepRootsVisible && this.rootKeys.has(key);

    // Background roots use polygonOffset so detail tiles win the Z-test without flickering.
    // Assumes createTileMesh returns a per-instance material (required by WorldMapping contract).
    if (isBackgroundRoot) {
      const mat = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
      const bg = mat.clone();
      bg.polygonOffset = true;
      bg.polygonOffsetFactor = 0;
      bg.polygonOffsetUnits = 4;
      if (Array.isArray(mesh.material)) {
        mesh.material[0] = bg;
      } else {
        mesh.material = bg;
      }
    }

    mesh.userData = {
      coord,
      lastUsedFrame: this.frame,
      state: 'loading',
      isBackgroundRoot,
      baseRenderOrder: mesh.renderOrder,
    };
    this.applyTileSettings(mesh);

    this.tilesGroup.add(mesh);

    return this.cache
      .getOrCreateTexture(key, (signal) => this.loader.loadTileTexture(coord, signal), priority)
      .then((texture) => {
        if (!mesh.parent) {
          return;
        }
        this.worldMapping.applyTexture(mesh, texture);
        this.applyTileSettings(mesh);
        mesh.userData.state = 'ready';
        // Visibility will be set by applyVisibility() based on selection
      })
      .catch((err: unknown) => {
        // Distinguish deliberate cancellations from real network errors.
        // Cancelled tiles must NOT be blacklisted — they are retryable next frame.
        if ((err as { name?: string })?.name === 'AbortError') {
          mesh.userData.state = 'cancelled';
        } else {
          mesh.userData.state = 'error';
          this.failedTiles.add(key);
        }
      });
  }

  private forEachTile(callback: (mesh: Mesh) => void): void {
    for (const child of this.tilesGroup.children) {
      callback(child as Mesh);
    }
  }

  private applyTileSettings(mesh: Mesh): void {
    const coord = mesh.userData.coord as TileCoord;
    const translucent = this.opacity < 1;

    // Tiles don't write depth, so three draws them in creation order. Finer tiles draw last, so
    // a fallback parent created after its children doesn't cover them and show them blurry.
    // A translucent tile can't cover its fallbacks, so then finer tiles draw first and mark
    // the stencil, and coarser tiles only fill in where nothing is drawn yet.
    // The roots don't overlap, so shown alone they need no stencil, which would also keep an
    // orthophoto drawn over them from showing.
    const rootsOnly = this.rootsOnlyRenderOrder !== undefined;
    mesh.renderOrder =
      this.rootsOnlyRenderOrder ??
      (mesh.userData.baseRenderOrder as number) +
        (translucent ? -1 : 1) * coord.z * ZOOM_RENDER_ORDER_STEP;

    const materials: Material[] = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const material of materials) {
      material.opacity = this.opacity;
      material.stencilWrite = translucent && !rootsOnly;
      material.stencilRef = 1;
      material.stencilFunc = NotEqualStencilFunc;
      material.stencilZPass = ReplaceStencilOp;

      if (material instanceof MeshBasicMaterial) {
        for (const texture of [material.map, material.alphaMap]) {
          if (texture && texture.magFilter !== this.magFilter) {
            texture.magFilter = this.magFilter;
            texture.needsUpdate = true;
          }
        }
      }
    }
  }

  private markVisible(mesh: Mesh): void {
    mesh.userData.lastUsedFrame = this.frame;
    this.visibleKeys.add(mesh.name); // only mark; don't set mesh.visible here
    // Keep shown textures at the recent end of the LRU so eviction takes unused ones first
    this.cache.touch(mesh.name);
  }

  private applyVisibility(): void {
    for (const child of this.tilesGroup.children) {
      const mesh = child as Mesh;
      const ready = this.isReady(mesh);
      const selectedNow = this.visibleKeys.has(mesh.name);
      const lastSelectedFrame = this.lastSelectedFrameByKey.get(mesh.name);
      const withinHold =
        lastSelectedFrame !== undefined && this.frame - lastSelectedFrame <= HOLD_FRAMES;
      const isBackground = !!mesh.userData?.isBackgroundRoot;
      mesh.visible = ready && (selectedNow || withinHold || isBackground);
    }
  }

  // ------------------------------------------------------------
  // Cleanup / eviction
  // ------------------------------------------------------------

  private cleanup(): void {
    const KEEP_FRAMES = 60;

    for (const child of [...this.tilesGroup.children]) {
      const mesh = child as Mesh;
      const { lastUsedFrame, state, isBackgroundRoot } = mesh.userData;

      // Cancelled tiles are removed immediately so they can be retried on the next
      // frame they are selected (they are not added to failedTiles).
      const isCancelled = state === 'cancelled';
      const isStale = !this.visibleKeys.has(mesh.name) && this.frame - lastUsedFrame > KEEP_FRAMES;
      // Background roots in ready/loading state are permanent — skip stale cleanup for them.
      // Error and cancelled states still follow normal cleanup so they can be retried/blacklisted.
      const isPermanent = !!isBackgroundRoot && state !== 'error' && state !== 'cancelled';

      if (isCancelled || (isStale && !isPermanent)) {
        if (state === 'error') {
          this.failedTiles.add(mesh.name);
        }
        this.worldMapping.disposeTileMesh(mesh);
        this.tilesGroup.remove(mesh);
      }
    }
  }

  // ------------------------------------------------------------
  // Priority scoring
  // ------------------------------------------------------------

  /**
   * Compute a [0, 1] priority score for a tile based on its screen-space distance from
   * the viewport center. Tiles closer to the center of the screen load first.
   * Works correctly for both perspective and orthographic cameras.
   */
  private computeTilePriority(coord: TileCoord, camera: Camera): number {
    const center = this.getTileCenter(coord);
    const ndc = center.clone().project(camera);
    const screenDist = Math.sqrt(ndc.x * ndc.x + ndc.y * ndc.y);
    return 1 / (1 + screenDist);
  }

  // ------------------------------------------------------------
  // Screen-size estimation & frustum culling
  // ------------------------------------------------------------

  private estimateTileScreenSize(
    coord: TileCoord,
    camera: Camera,
    renderer: WebGLRenderer,
  ): number {
    const corners = this.worldMapping.getTileWorldCorners(coord);
    return this.worldMapping.estimateScreenPx(corners, camera, renderer);
  }

  private tileIntersectsFrustum(coord: TileCoord, camera: Camera): boolean {
    const margin = FRUSTUM_NDC_MARGIN;
    const corners = this.worldMapping.getTileWorldCorners(coord);
    const pts = corners.map((p) => p.clone().project(camera));

    // If all points are behind camera / beyond far clip, reject
    if (pts.every((p) => p.z > 1)) {
      return false;
    }

    // If all points are outside on same side in NDC (with margin), reject.
    // Margin keeps a buffer of tiles just outside view for streaming/LOD (avoids seeing edges).
    if (pts.every((p) => p.x < -1 - margin)) {
      return false;
    }
    if (pts.every((p) => p.x > 1 + margin)) {
      return false;
    }
    if (pts.every((p) => p.y < -1 - margin)) {
      return false;
    }
    if (pts.every((p) => p.y > 1 + margin)) {
      return false;
    }

    return true;
  }

  private getTileCenter(coord: TileCoord): Vector3 {
    const corners = this.worldMapping.getTileWorldCorners(coord);
    // Compute center as average of corners
    const center = new Vector3();
    for (const corner of corners) {
      center.add(corner);
    }
    center.divideScalar(corners.length);
    return center;
  }
}
